// src/utils/action-utils.js

/**
 * Generates a human-readable description of an action
 * 
 * @param {Object} action - The action object
 * @returns {string} Human-readable description
 */
export const getActionDescription = (action) => {
    if (!action) return '';

    let text = '';
    let hobjectName = '';
    let othersHobject = '';

    // Set the hobject name and additional objects text
    if (action.target_all_objects === true) {
        hobjectName = 'All objects';
    } else if (action.targets && action.targets.length > 0) {
        hobjectName = action.targets[0] && action.targets[0].text ? action.targets[0].text : '';

        if (action.targets.length === 2) {
            othersHobject = `(+ ${action.targets.length - 1})`;
        } else if (action.targets.length > 2) {
            othersHobject = `(+ ${action.targets.length - 1})`;
        }
    }

    const verbTermination = (action.targets && action.targets.length === 1) ? 'S' : '';

    // Generate appropriate description based on action type
    switch (action.type) {
        case 'action_show':
            text = `${hobjectName} ${othersHobject} ENTER${verbTermination}`;
            break;
        case 'action_hide':
            text = `${hobjectName} ${othersHobject} EXIT${verbTermination}`;
            break;
        case 'action_toggle_show_hide':
            text = `TOGGLES SHOW/HIDE on ${hobjectName} ${othersHobject} `;
                break;
        case 'action_play':
            text = `${hobjectName} ${othersHobject} PLAY${verbTermination}`;
            break;
        case 'action_pause':
            text = `${hobjectName} ${othersHobject} PAUSE${verbTermination}`;
            break;
        case 'action_toggle_play_pause':
            text = `${hobjectName} ${othersHobject} TOGGLE PLAY/PAUSE${verbTermination}`;
            break;
        case 'action_stop':
            text = `${hobjectName} ${othersHobject} STOP${verbTermination}`;
            break;
        case 'action_seek':
            text = `SET START TIME on ${hobjectName} ${othersHobject} at ${action.seconds} sec`;
            break;
        case 'action_end':
            text = `FORCE ${hobjectName} ${othersHobject} to END`;
            break;
        case 'action_set_materials_variant':
            text = `SET MATERIALS VARIANT on ${hobjectName} ${othersHobject}`;
            break;
        case 'action_animate':
            text = `PLAY ANIMATION on ${hobjectName} ${othersHobject}`;
            break;
        case 'action_spin':
            text = `${hobjectName} ${othersHobject} SPIN${verbTermination}`;
            break;
        case 'action_orbit':
            text = `${hobjectName} ${othersHobject} ORBIT${verbTermination}`;
            break;
        case 'action_transform_to':
            text = 'Move, Rotate, Scale to';
            break;
        case 'action_transform_by':
            text = 'Move, Rotate, Scale by';
            break;
        case 'action_move_in_direction':
            text = `MOVE ${hobjectName} ${othersHobject} in ${action.direction} direction`;
            break;
        case 'action_play_particles':
            text = `PLAY PARTICLES on ${hobjectName} ${othersHobject}`;
            break;
        case 'action_change_space':
            text = 'CHANGE SPACE';
            break;
        case 'action_force_end_sequence':
            text = 'FORCE END SEQUENCE';
            break;
        case 'action_show_instructions':
            text = 'SHOW INSTRUCTIONS';
            break;
        case 'action_open_home':
            text = 'OPEN HOME PAGE';
            break;
        case 'action_open_url':
            text = 'OPEN LINK';
            break;
        case 'action_raise_event':
            text = `RAISE EVENT ${action.event_message || ''}`;
            break;
        case 'action_set_experience_origin':
            text = 'RESET USER ORIGIN';
            break;
        case 'action_start_following':
            text = `Attach ${hobjectName} ${othersHobject} to Screen`;
            break;
        case 'action_stop_following':
            text = `Detach ${hobjectName} ${othersHobject} from Screen`;
            break;
        case 'action_history_back':
            text = 'Back to previous space';
            break;
        case 'trigger_image_anchor_found':
            text = 'WAIT for user to SCAN IMAGE';
            break;
        case 'trigger_tap':
            text = 'WAIT for user to TAP object';
            break;
        case 'trigger_screen_tap':
            text = 'WAIT for user to TAP SCREEN';
            break;
        default:
            text = `${action.type}`;
    }

    return text;
};

export const getThumbnailForNode = (nodePid) => {
    if (!nodePid) {
        console.warn('getThumbnailForNode: No nodePid provided');
        return [];
    }

    // Add logging to debug the nodes array
    console.log(`Looking for node with pid ${nodePid} in nodes array:`,
        this.nodes ? this.nodes.length : 'nodes is null/undefined');

    const node = this.nodes ? this.nodes.find(n => n.pid === nodePid) : null;
    if (!node) {
        console.warn(`No node found with pid ${nodePid}`);
        return [];
    }

    const hobject = this.hobjects ? this.hobjects.find(h => h.pid === node.hobject_pid) : null;
    if (!hobject) {
        console.warn(`No hobject found with pid ${node.hobject_pid}`);
        return [];
    }

    try {
        const abilities = JSON.parse(hobject.abilities);
        if (abilities.gltf_loader && abilities.gltf_loader.animation_clip_names) {
            return abilities.gltf_loader.animation_clip_names;
        }
    } catch (e) {
        console.error('Error parsing hobject abilities:', e);
    }

    return [];
};


/**
  * Gets the hobject images associated with an action
  * 
  * @param {Object} action - The action object
  * @returns {Array} Array of image URLs or names
  */
export const getActionHobjectImages = (action) => {
    if (!action || !action.targets || !action.targets[0]) {
        return {};
    }
    const images = [];
    if (action.target_all_objects) {
        images.push('All objects');
    } else if (action.targets && action.targets.length > 0) {
        action.targets.forEach((target) => {
            if (target.image) {
                images.push(target.image);
            }
        });
    }

    return images;
}


/**
 * Gets the appropriate CSS class for an action based on its start type
 * 
 * @param {Object} action - The action object
 * @returns {string} CSS class name
 */
export const getActionClass = (action) => {
    if (!action || !action.start) return '';
    return action.start.value === 'after_previous' ? 'after-previous' : 'with-previous';
};

/**
 * Checks if an action has any timing-related properties
 * 
 * @param {Object} action - The action object
 * @returns {boolean} Whether the action has timing properties
 */
export const hasTimingProperties = (action) => {
    if (!action) return false;

    return Object.prototype.hasOwnProperty.call(action, 'duration') ||
        Object.prototype.hasOwnProperty.call(action, 'delay') ||
        Object.prototype.hasOwnProperty.call(action, 'wait_until_finished');
};

/**
 * Checks if an action can target objects
 * 
 * @param {Object} action - The action object
 * @returns {boolean} Whether the action can target objects
 */
export const canTargetObjects = (action) => {
    if (!action) return false;

    const nonTargetingActions = [
        'action_change_space',
        'action_open_home',
        'action_force_end_sequence',
        'action_set_experience_origin',
        'action_open_url',
        'action_show_instructions',
        'action_send_amqp_message',
        'action_send_mqtt_message',
        'action_raise_event',
        'action_history_back',
        'trigger_image_anchor_found',
        'trigger_screen_tap',
    ];

    return !nonTargetingActions.includes(action.type);
};

/**
 * Validates an action to ensure it has all required properties
 * 
 * @param {Object} action - The action object to validate
 * @returns {Object} An object with isValid and any error messages
 */
export const validateSequenceElement = (seq_element) => {
    if (!seq_element) {
        return { isValid: false, errors: ['Action is undefined'] };
    }

    const errors = [];

    // Check required common properties
    if (!seq_element.id) errors.push('Action id is missing');
    if (!seq_element.type) errors.push('Action type is missing');

    // Check targets for seq_elements that need them
    if (canTargetObjects(seq_element) &&
        !seq_element.target_all_objects &&
        (!seq_element.targets || seq_element.targets.length === 0)) {
        errors.push('Action requires targets but none are specified');
    }

    // Type-specific validations
    switch (seq_element.type) {
        case 'action_seek':
            if (seq_element.seconds === undefined) errors.push('Seek action requires seconds property');
            break;
        case 'action_open_url':
            if (!seq_element.url) errors.push('Open URL action requires a URL');
            break;
        case 'action_show_instructions':
            if (!seq_element.instructions_text) errors.push('Instructions text is required for this action');
            break;
        case 'action_raise_event':
            if (!seq_element.event_message) errors.push('Raise event requires an event message');
            break;
        case 'action_force_end_sequence':
            if (!seq_element.sequence_id) errors.push('A sequence ID is required to force end a sequence');
                break;
        // Add more type-specific validations as needed
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};